<template>
  <div>
    <div v-if="!hq" class="text-center py-5 text-secondary">
      Create the HQ budget first (Manage Budgets) to see budget visualizations for {{ year }}.
    </div>

    <div v-else>
      <!-- Control bar -->
      <div class="d-flex flex-wrap gap-2 align-items-center bg-semi-transparent rounded p-2 mb-3">
        <select class="form-select form-select-sm w-auto" v-model="timeMode">
          <option value="year">Full year</option>
          <option value="month">Per month</option>
          <option value="cumulative">Cumulative</option>
        </select>
        <select class="form-select form-select-sm w-auto" v-model="districtSel">
          <option value="all">All districts</option>
          <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
        </select>
        <button v-if="view === 'table'" class="btn btn-sm btn-outline-success ms-auto" @click="downloadExcel" title="Download as Excel Sheet">
          <i class="fa-solid fa-file-excel me-1" /> Download as Excel Sheet
        </button>
        <div class="btn-group btn-group-sm" :class="{ 'ms-auto': view !== 'table' }">
          <button class="btn" :class="view === 'chart' ? 'btn-primary' : 'btn-outline-primary'" @click="view = 'chart'">
            <i class="fa-solid fa-chart-column" />
          </button>
          <button class="btn" :class="view === 'table' ? 'btn-primary' : 'btn-outline-primary'" @click="view = 'table'">
            <i class="fa-solid fa-table" />
          </button>
        </div>
      </div>

      <kpi-cards :cards="kpiCards" />

      <!-- TABLE VIEW -->
      <div v-if="view === 'table'" class="table-responsive hide-scrollbar">
        <!-- Month matrix -->
        <table v-if="isMonthMode" class="table table-dark table-sm align-middle text-nowrap expense-matrix mb-0">
          <thead>
            <tr>
              <th class="cat-col">{{ matrix.rowLabel }}</th>
              <th v-for="m in monthLabels" :key="m.num" class="text-end">{{ m.label }}</th>
              <th class="text-end used-col">Used</th>
              <th class="text-end alloc-col">Allocated</th>
              <th class="text-end remain-col">Remaining</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in matrix.rows" :key="r.key" :class="{ 'text-secondary': r.indented }">
              <td class="cat-col fw-semibold" :class="{ 'ps-4': r.indented }" :style="r.isKerjaRutin ? 'cursor: pointer' : null"
                @click="r.isKerjaRutin ? (kerjaRutinExpanded = !kerjaRutinExpanded) : null">
                <i v-if="r.isKerjaRutin" class="fas me-2" :class="kerjaRutinExpanded ? 'fa-chevron-down' : 'fa-chevron-right'" />{{ r.label }}
              </td>
              <td v-for="(v, i) in r.months" :key="i" class="text-end">
                <span v-if="v">{{ fmt(v) }}</span><span v-else class="text-secondary">&ndash;</span>
              </td>
              <td class="text-end used-col fw-semibold">{{ fmt(r.used) }}</td>
              <td class="text-end alloc-col">{{ r.allocated == null ? "—" : fmt(r.allocated) }}</td>
              <td class="text-end remain-col" :class="r.remaining == null ? '' : (r.remaining < 0 ? 'text-danger' : 'text-success')">{{ r.remaining == null ? "—" : fmt(r.remaining) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="fw-bold">
              <td class="cat-col">Total</td>
              <td v-for="(v, i) in matrix.monthTotals" :key="i" class="text-end">{{ fmt(v) }}</td>
              <td class="text-end used-col">{{ fmt(matrix.total.used) }}</td>
              <td class="text-end alloc-col">{{ fmt(matrix.total.allocated) }}</td>
              <td class="text-end remain-col" :class="matrix.total.remaining < 0 ? 'text-danger' : 'text-success'">{{ fmt(matrix.total.remaining) }}</td>
            </tr>
          </tfoot>
        </table>

        <!-- Full-year breakdown -->
        <table v-else class="table table-dark table-sm table-bordered align-middle mb-0">
          <thead>
            <tr>
              <th>{{ fullYear.rowLabel }}</th>
              <th class="text-end">Allocated</th>
              <th class="text-end">Used</th>
              <th class="text-end">%</th>
              <th class="text-end">Remaining</th>
              <th class="text-end">%</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in fullYear.rows">
              <tr v-if="r.isWpsHeader" :key="'wps'" class="fw-bold" style="cursor: pointer" @click="wpsExpanded = !wpsExpanded">
                <td><i class="fas me-2" :class="wpsExpanded ? 'fa-chevron-down' : 'fa-chevron-right'" />WPS (Subtotal)</td>
                <td class="text-end">{{ fmt(r.allocated) }}</td>
                <td class="text-end">{{ fmt(r.used) }}</td>
                <td class="text-end">{{ pct(r.used, r.allocated) }}</td>
                <td class="text-end">{{ fmt(r.remaining) }}</td>
                <td class="text-end">{{ pct(r.remaining, r.allocated) }}</td>
              </tr>
              <tr v-else :key="r.key" :class="{ 'text-secondary': r.indented }">
                <td :class="{ 'ps-5': r.indented }" :style="r.isKerjaRutin ? 'cursor: pointer' : null"
                  @click="r.isKerjaRutin ? (kerjaRutinExpanded = !kerjaRutinExpanded) : null">
                  <i v-if="r.isKerjaRutin" class="fas me-2" :class="kerjaRutinExpanded ? 'fa-chevron-down' : 'fa-chevron-right'" />{{ r.label }}
                </td>
                <td class="text-end">{{ r.allocated == null ? "—" : fmt(r.allocated) }}</td>
                <td class="text-end">{{ fmt(r.used) }}</td>
                <td class="text-end">{{ r.allocated == null ? "—" : pct(r.used, r.allocated) }}</td>
                <td class="text-end">{{ r.remaining == null ? "—" : fmt(r.remaining) }}</td>
                <td class="text-end">{{ r.allocated == null ? "—" : pct(r.remaining, r.allocated) }}</td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr class="fw-bold table-active">
              <td>Total</td>
              <td class="text-end">{{ fmt(fullYear.total.allocated) }}</td>
              <td class="text-end">{{ fmt(fullYear.total.used) }}</td>
              <td class="text-end">{{ pct(fullYear.total.used, fullYear.total.allocated) }}</td>
              <td class="text-end">{{ fmt(fullYear.total.remaining) }}</td>
              <td class="text-end">{{ pct(fullYear.total.remaining, fullYear.total.allocated) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- CHART VIEW: sections of charts whose layout depends on Time + District -->
      <div v-else>
        <!-- Subtabs: only when all districts (focus one category at a time) -->
        <ul v-if="districtSel === 'all'" class="nav subtab-strip gap-1">
          <li class="nav-item" v-for="t in subtabs" :key="t.key">
            <button class="nav-link subtab" :class="activeSubtab === t.key ? 'active' : ''" :style="subtabStyle()" @click="subtab = t.key">{{ t.label }}</button>
          </li>
        </ul>
        <!-- The active subtab's colour runs along this panel's top edge, so the
             tab reads as joined to the charts it selects (Hanif, 16 Oct). -->
        <div :class="{ 'subtab-panel': districtSel === 'all' }" :style="districtSel === 'all' ? subtabStyle() : null">
        <p v-if="timeMode === 'year'" class="small text-secondary mb-2">Donuts show actual usage of allocation (Used vs Remaining).</p>
        <div v-if="!chartBlocks.length" class="text-center text-secondary py-5">No data to chart.</div>
        <div v-else>
          <div v-for="(s, si) in chartSections" :key="si" class="mb-4">
            <h6 v-if="s.label" class="text-secondary small text-uppercase mb-2">{{ s.label }}</h6>
            <div :class="s.rowClass">
              <div v-for="b in s.blocks" :key="b.key" :class="b.col">
                <div class="bg-semi-transparent rounded p-2 h-100">
                  <div class="small text-secondary text-center mb-1">{{ b.title }}</div>
                  <viz-chart :type="b.type" :data="b.data" :options="b.options" :plugins="b.plugins" :height="b.height" />
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KpiCards from "@/components/viz/KpiCards";
import VizChart from "@/components/viz/VizChart";
import { CATEGORY_LABELS, DISTRICT_CATEGORIES } from "@/utils/budget";
import {
  budgetCategoryView,
  budgetDistrictView,
  budgetMonthMatrix,
  allocationForCategory,
  usedForCategory,
  districtsWithUnassigned,
  monthlyUsed,
} from "@/utils/dashboard";
import { downloadXlsx } from "@/utils/exportTable";

// Folder-index tabs for the chart subtabs share one accent colour: Hanif chose
// "one accent" from four palettes on 19 Oct, because a colour per category
// read as a rainbow. The active tab is full colour, the rest dimmed, and the
// label alone names the category. Red and green stay off the tabs: the donuts
// use them. Contrast on the page's #000: #111 on the fill is 6.4:1, and white
// on the dimmed (0.6 alpha) fill is 6.9:1.
const SUBTAB_COLOR = "#4C9BE8";

// Compact RM for the amount above each bar: "RM 1.2M", "RM 350K", "RM 12".
// Rounds first so 999,999 reads "RM 1M", never "RM 1000K".
function compactRM(v) {
  const n = Math.round(Math.abs(Number(v) || 0));
  const sign = Number(v) < 0 ? "-" : "";
  const trim = (x) => String(parseFloat(x.toFixed(1)));
  if (n >= 999950) return `${sign}RM ${trim(n / 1e6)}M`;
  if (n >= 1000) return `${sign}RM ${trim(n / 1e3)}K`;
  return `${sign}RM ${n}`;
}

// Draws each BAR's value just above it (Hanif, 19 Oct). Inline rather than
// chartjs-plugin-datalabels to avoid a new dependency. Only datasets Chart.js
// resolves to "bar" are labelled, so the dashed "Expected" lines stay clean.
// All or nothing per chart: amounts are drawn only if every one fits within
// its own bar's width. Grouped charts (12 months x many series) have bars a
// few px wide, and labelling just the bars that happen to fit reads as a bug,
// so such charts keep values in the tooltip only. Headroom for
// the tallest label comes from the y-axis `grace`, not layout padding, which
// would add space above the legend and leave the label on top of it.
const BAR_VALUE_LABELS = {
  id: "barValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = "#e9ecef"; // light text: the card background is dark
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    const labels = [];
    chart.data.datasets.forEach((ds, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.type !== "bar" || !chart.isDatasetVisible(i)) return;
      meta.data.forEach((bar, j) => {
        const v = Number(ds.data[j]);
        if (!v) return; // zero / empty bars show nothing
        labels.push({ bar, text: compactRM(v) });
      });
    });
    const allFit = labels.every(({ bar, text }) => ctx.measureText(text).width <= bar.width + 4);
    if (allFit) labels.forEach(({ bar, text }) => ctx.fillText(text, bar.x, bar.y - 4));
    ctx.restore();
  },
};

// Categories with no district dimension at all (HQ-only, allocation AND usage).
// kerja_rutin is deliberately NOT here — its allocation is HQ-only but usage
// now splits by district (see DISTRICT_CATEGORIES-like handling below).
const NON_SPLIT_CATEGORIES = ["kos_awalan", "wps_pca", "wps_irms", "wps_tot"];
// Utilization pie colors (Used vs Remaining) — green / red, matching
// Sebut Harga's allocation chart coding (Belanja hijau, Baki merah).
const USED_COLOR = "#00d462";
const REMAINING_COLOR = "#dc3545";

export default {
  name: "budget-viz-tab",
  props: ["selectedYear", "hq", "lines", "budgets", "expenses", "cases", "districts"],
  components: { KpiCards, VizChart },
  data() {
    return {
      timeMode: "year",
      districtSel: "all",
      subtab: "wps_nod",
      view: "chart",
      wpsExpanded: true,
      kerjaRutinExpanded: true,
    };
  },
  computed: {
    year() {
      return Number(this.selectedYear || this.Helper.currentYear);
    },
    scope() {
      return {
        lines: this.lines,
        budgets: this.budgets,
        cases: this.cases,
        expenses: this.expenses,
        districtId: this.districtSel,
        year: this.year,
        moment: this.Helper.moment,
      };
    },
    isMonthMode() {
      return this.timeMode === "month" || this.timeMode === "cumulative";
    },
    // Chart-view subtabs (District = All). Full year keeps the small categories in
    // one "Others" tab (just a few pies); per-month / cumulative needs one
    // self-scaled tab per category, so each gets its own subtab.
    subtabs() {
      const splittable = [
        { key: "wps_nod", label: "NOD" },
        { key: "wps_emergency", label: "Emergency" },
        { key: "wps_electrical", label: "Electrical" },
        { key: "kerja_rutin", label: "Kerja Rutin" },
      ];
      if (this.timeMode === "year") {
        return splittable.concat([{ key: "others", label: "Others" }]);
      }
      return splittable.concat([
        { key: "wps_pca", label: "PCA" },
        { key: "wps_irms", label: "IRMS" },
        { key: "wps_tot", label: "ToT" },
        { key: "kos_awalan", label: "Kos Awalan" },
      ]);
    },
    // Clamp the stored subtab to one valid for the current mode's subtab set.
    activeSubtab() {
      return this.subtabs.some((t) => t.key === this.subtab) ? this.subtab : this.subtabs[0].key;
    },
    monthLabels() {
      return Array.from({ length: 12 }, (_, i) => ({
        num: i + 1,
        label: this.Helper.moment(`${this.year}-${String(i + 1).padStart(2, "0")}-01`).format("MMM"),
      }));
    },
    // --- Full-year table model (category breakdown, WPS group + subtotal + total) ---
    fullYear() {
      const v = budgetCategoryView(this.scope);
      const out = [];
      v.rows.forEach((r) => {
        if (r.group === "wps") {
          if (!out.some((x) => x.isWpsHeader)) out.push(Object.assign({ isWpsHeader: true }, v.wpsSubtotal));
          if (this.wpsExpanded) out.push(Object.assign({ key: r.category, indented: true }, r));
        } else if (r.category === "kerja_rutin" && this.districtSel === "all") {
          // Per-district expand/collapse only makes sense at "all districts"
          // scope; at a specific district it's just a plain row (falls to
          // the final else below) since there's nothing left to expand into.
          out.push(Object.assign({ key: r.category, isKerjaRutin: true }, r));
          if (this.kerjaRutinExpanded) out.push(...this.kerjaRutinDistrictRows());
        } else {
          out.push(Object.assign({ key: r.category }, r));
        }
      });
      return { rowLabel: "Category", rows: out, total: v.total };
    },
    // Same shape as `fullYear.rows` but Kerja Rutin's district breakdown is
    // always expanded — the Excel export is a complete snapshot, independent
    // of the on-screen kerjaRutinExpanded toggle. WPS's existing wpsExpanded-
    // driven export behavior is intentionally left as-is.
    fullYearExportRows() {
      const v = budgetCategoryView(this.scope);
      const out = [];
      v.rows.forEach((r) => {
        if (r.group === "wps") {
          if (!out.some((x) => x.isWpsHeader)) out.push(Object.assign({ isWpsHeader: true }, v.wpsSubtotal));
          if (this.wpsExpanded) out.push(Object.assign({ key: r.category, indented: true }, r));
        } else if (r.category === "kerja_rutin" && this.districtSel === "all") {
          out.push(Object.assign({ key: r.category }, r));
          out.push(...this.kerjaRutinDistrictRows());
        } else {
          out.push(Object.assign({ key: r.category }, r));
        }
      });
      return out;
    },
    // --- Month matrix model (category x month) ---
    matrix() {
      const cumulative = this.timeMode === "cumulative";
      const m = budgetMonthMatrix(this.scope, { rowDim: "category", cumulative });
      const rows = [];
      m.rows.forEach((r) => {
        // Per-district expand/collapse only applies at "all districts" scope.
        const isKerjaRutin = r.key === "kerja_rutin" && this.districtSel === "all";
        rows.push(Object.assign({ isKerjaRutin }, r));
        if (isKerjaRutin && this.kerjaRutinExpanded) {
          rows.push(...this.kerjaRutinMonthDistrictRows(cumulative));
        }
      });
      return { rowLabel: "Category", rows, monthTotals: m.monthTotals, total: m.total };
    },
    // Same shape as `matrix.rows` but Kerja Rutin's district breakdown is
    // always expanded for the export, independent of the on-screen toggle.
    monthExportRows() {
      const cumulative = this.timeMode === "cumulative";
      const m = budgetMonthMatrix(this.scope, { rowDim: "category", cumulative });
      const rows = [];
      m.rows.forEach((r) => {
        rows.push(r);
        if (r.key === "kerja_rutin" && this.districtSel === "all") {
          rows.push(...this.kerjaRutinMonthDistrictRows(cumulative));
        }
      });
      return rows;
    },
    kpiCards() {
      const t = this.isMonthMode ? this.matrix.total : this.fullYear.total;
      return [
        { label: "Allocated", value: this.Helper.formatMoney(t.allocated), variant: "success" },
        { label: "Used", value: this.Helper.formatMoney(t.used), sub: this.pct(t.used, t.allocated) + " of allocated", variant: "primary" },
        { label: "Remaining", value: this.Helper.formatMoney(t.remaining), sub: this.pct(t.remaining, t.allocated) + " left", variant: t.remaining < 0 ? "danger" : "warning" },
      ];
    },
    // The chart view: a list of chart blocks (each with a section + grid column).
    //  - Full year      -> utilization pies (Used vs Remaining), grouped into
    //                       "districts" and "other categories" sections.
    //  - Per month/cumu  -> grouped bars of usage per month, with a dashed
    //                       guidance line = expected pace (allocation / 12).
    chartBlocks() {
      const cumulative = this.timeMode === "cumulative";
      const monthLbls = this.monthLabels.map((m) => m.label);
      const all = this.districtSel === "all";
      const blocks = [];

      // ----- FULL YEAR: utilization pies (+ Kerja Rutin's district bar) -----
      if (this.timeMode === "year") {
        if (!all) {
          // specific district: 3 pies (NOD / Emergency / Electrical) + a
          // Kerja Rutin bar (no ceiling, so no donut), all centered together
          DISTRICT_CATEGORIES.forEach((cat) => {
            blocks.push(this.pieBlock(`c-${cat}`, CATEGORY_LABELS[cat], usedForCategory(cat, this.scope) || 0, allocationForCategory(cat, this.scope) || 0, "NOD / Emergency / Electrical", "row g-3 justify-content-center"));
          });
          blocks.push(this.districtBarBlock("kerja_rutin"));
        } else if (this.activeSubtab === "others") {
          NON_SPLIT_CATEGORIES.forEach((cat) => {
            blocks.push(this.pieBlock(`c-${cat}`, CATEGORY_LABELS[cat], usedForCategory(cat, this.scope) || 0, allocationForCategory(cat, this.scope) || 0, "Other categories", "row g-3"));
          });
        } else {
          // One large HQ donut on top (Hanif, 19 Oct: "All district = HQ"),
          // then the district detail below it. HQ uses the HQ ceiling, the same
          // figure as the Allocated card, so it can exceed the district shares
          // added up (the remainder is not yet given to any district).
          const cat = this.activeSubtab;
          blocks.push(this.pieBlock(`hq-${cat}`, "HQ — all districts", usedForCategory(cat, this.scope) || 0, allocationForCategory(cat, this.scope) || 0, `${CATEGORY_LABELS[cat]} — HQ`, "row g-3 justify-content-center", true));
          if (cat === "kerja_rutin") {
            // no per-district ceiling, so the detail is the district bar chart
            blocks.push(this.districtBarBlock("kerja_rutin"));
          } else {
            this.districts.forEach((d) => {
              blocks.push(this.pieBlock(`d-${d.id}`, d.name, usedForCategory(cat, this.dScope(d.id)) || 0, allocationForCategory(cat, this.dScope(d.id)) || 0, `${CATEGORY_LABELS[cat]} — by district`, "row g-3"));
            });
          }
        }
        return blocks;
      }

      // ----- PER MONTH / CUMULATIVE: grouped bars + Expected line(s) -----
      const m = budgetMonthMatrix(this.scope, { rowDim: "category", cumulative });
      const barBlock = (key, title, cats, col) => ({
        key,
        section: "",
        rowClass: "row g-3",
        title,
        type: "bar",
        data: {
          labels: monthLbls,
          datasets: cats
            .map((cat, i) => {
              const row = m.rows.find((r) => r.key === cat) || { label: CATEGORY_LABELS[cat], months: new Array(12).fill(0) };
              return { label: row.label, data: row.months, backgroundColor: this.palette(i) };
            })
            .concat(this.guidanceLines(cats, cumulative)),
        },
        options: this.monthAxisOptions(),
        plugins: [BAR_VALUE_LABELS],
        col: col || "col-12",
      });

      if (!all) {
        blocks.push(barBlock("split-bars", "Monthly usage (NOD / Emergency / Electrical)", DISTRICT_CATEGORIES));
        blocks.push(this.singleCategoryMonthBarBlock("kerja_rutin", cumulative));
      } else if (this.activeSubtab === "kerja_rutin") {
        blocks.push(this.districtMonthBarBlock("kerja_rutin", cumulative));
      } else {
        // one category per subtab, self-scaled (splittable = summed over districts).
        const cat = this.activeSubtab;
        const note = DISTRICT_CATEGORIES.indexOf(cat) !== -1 ? ", all districts" : "";
        blocks.push(barBlock(`m-${cat}`, `Monthly usage (${CATEGORY_LABELS[cat]}${note})`, [cat]));
      }
      return blocks;
    },
    // Group the chart blocks into ordered sections for rendering.
    chartSections() {
      const sections = [];
      this.chartBlocks.forEach((b) => {
        const label = b.section || "";
        let s = sections.find((x) => x.label === label);
        if (!s) {
          s = { label, rowClass: b.rowClass || "row g-3", blocks: [] };
          sections.push(s);
        }
        s.blocks.push(b);
      });
      return sections;
    },
    // Headers + rows for the Excel export, mirroring whichever table is shown.
    // Money cells are raw numbers so Excel keeps them summable; % stays text.
    excelExport() {
      if (this.isMonthMode) {
        const months = this.monthLabels.map((m) => m.label);
        const headers = [this.matrix.rowLabel, ...months, "Used", "Allocated", "Remaining"];
        const rows = this.monthExportRows.map((r) => [
          r.indented ? "    " + r.label : r.label,
          ...r.months.map((v) => v || 0),
          r.used,
          r.allocated == null ? "" : r.allocated,
          r.remaining == null ? "" : r.remaining,
        ]);
        rows.push([
          "Total",
          ...this.matrix.monthTotals.map((v) => v || 0),
          this.matrix.total.used,
          this.matrix.total.allocated,
          this.matrix.total.remaining,
        ]);
        return { sheetName: "Budget", filename: `budget-${this.timeMode}-${this.year}.xlsx`, headers, rows };
      }
      const headers = [this.fullYear.rowLabel, "Allocated", "Used", "% Used", "Remaining", "% Remaining"];
      const rows = this.fullYearExportRows.map((r) => {
        const label = r.isWpsHeader ? "WPS (Subtotal)" : r.indented ? "    " + r.label : r.label;
        return [
          label,
          r.allocated == null ? "" : r.allocated,
          r.used,
          r.allocated == null ? "" : this.pct(r.used, r.allocated),
          r.remaining == null ? "" : r.remaining,
          r.allocated == null ? "" : this.pct(r.remaining, r.allocated),
        ];
      });
      const t = this.fullYear.total;
      rows.push(["Total", t.allocated, t.used, this.pct(t.used, t.allocated), t.remaining, this.pct(t.remaining, t.allocated)]);
      return { sheetName: "Budget", filename: `budget-year-${this.year}.xlsx`, headers, rows };
    },
  },
  methods: {
    // A subtab's colours as CSS custom properties: full colour for the active
    // tab and the panel edge, the same colour at 0.6 alpha for inactive tabs.
    subtabStyle() {
      const [r, g, b] = [1, 3, 5].map((i) => parseInt(SUBTAB_COLOR.slice(i, i + 2), 16));
      return { "--tab-color": SUBTAB_COLOR, "--tab-dimmed": `rgba(${r}, ${g}, ${b}, 0.6)` };
    },
    fmt(v) {
      return this.Helper.formatMoney(v || 0);
    },
    pct(part, whole) {
      const p = whole > 0 ? (part / whole) * 100 : 0;
      return `${p.toFixed(1)}%`;
    },
    palette(i) {
      const c = ["#0d6efd", "#198754", "#fd7e14", "#dc3545", "#6f42c1", "#20c997", "#ffc107", "#0dcaf0"];
      return c[i % c.length];
    },
    // Scope object scoped to one district.
    dScope(id) {
      return Object.assign({}, this.scope, { districtId: id });
    },
    // Per-district rows for Kerja Rutin's full-year expansion. Allocated/
    // Remaining are null (no per-district ceiling) — the template renders
    // "—" for those, never a misleading 0 or negative number.
    kerjaRutinDistrictRows() {
      const districts = districtsWithUnassigned(this.districts, this.expenses, "kerja_rutin", this.year);
      return budgetDistrictView(this.scope, "kerja_rutin", districts).rows.map((dr) => ({
        key: `kerja_rutin-${dr.districtId}`,
        indented: true,
        label: dr.label,
        allocated: dr.allocated,
        used: dr.used,
        remaining: dr.remaining,
      }));
    },
    // Per-district rows for Kerja Rutin's month/cumulative expansion. Same
    // null-preserving allocated/remaining as kerjaRutinDistrictRows.
    kerjaRutinMonthDistrictRows(cumulative) {
      const districts = districtsWithUnassigned(this.districts, this.expenses, "kerja_rutin", this.year);
      const dmm = budgetMonthMatrix(this.scope, { rowDim: "district", category: "kerja_rutin", districts, cumulative });
      return dmm.rows.map((dr) => ({
        key: `kerja_rutin-${dr.key}`,
        indented: true,
        label: dr.label,
        months: dr.months,
        allocated: dr.allocated,
        used: dr.used,
        remaining: dr.remaining,
      }));
    },
    // Options for a bar chart with districts on the x-axis (year mode).
    districtBarOptions() {
      return {
        plugins: {
          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${this.fmt(ctx.parsed.y)}` } },
        },
        scales: {
          y: { beginAtZero: true, grace: "10%", title: { display: true, text: "Amount (RM)" }, ticks: { callback: (v) => this.rmAxis(v) } }, // grace: room for the bar amounts
          x: { title: { display: true, text: "District" } },
        },
      };
    },
    // Bar chart of Used-by-district for a category with no per-district
    // ceiling (kerja_rutin) — a "Used vs Remaining" donut doesn't apply here
    // since there's nothing to ring against. Handles both "all districts"
    // (one bar per district) and a single selected district (one bar).
    districtBarBlock(category) {
      const all = this.districtSel === "all";
      let labels, data;
      if (all) {
        const districts = districtsWithUnassigned(this.districts, this.expenses, category, this.year);
        labels = districts.map((d) => d.name);
        data = districts.map((d) => usedForCategory(category, this.dScope(d.id)) || 0);
      } else {
        const d = this.districts.find((x) => x.id === this.districtSel);
        labels = [d ? d.name : "Selected district"];
        data = [usedForCategory(category, this.scope) || 0];
      }
      return {
        key: `dist-${category}`,
        section: all ? "" : "NOD / Emergency / Electrical",
        rowClass: all ? "row g-3" : "row g-3 justify-content-center",
        title: `${CATEGORY_LABELS[category]} · Used by district`,
        type: "bar",
        col: all ? "col-12" : "col-6 col-md-4 col-lg-3",
        data: { labels, datasets: [{ label: "Used", data, backgroundColor: this.palette(0) }] },
        options: this.districtBarOptions(),
        plugins: [BAR_VALUE_LABELS],
      };
    },
    // Grouped bars: one series per district, months on the x-axis (month/
    // cumulative mode, "all districts"). No guidance line — no per-district
    // ceiling to pace against.
    districtMonthBarBlock(category, cumulative) {
      const monthLbls = this.monthLabels.map((m) => m.label);
      const districts = districtsWithUnassigned(this.districts, this.expenses, category, this.year);
      const dmm = budgetMonthMatrix(this.scope, { rowDim: "district", category, districts, cumulative });
      return {
        key: `m-dist-${category}`,
        section: "",
        rowClass: "row g-3",
        title: `Monthly usage (${CATEGORY_LABELS[category]}, by district)`,
        type: "bar",
        col: "col-12",
        data: {
          labels: monthLbls,
          datasets: dmm.rows.map((r, i) => ({ label: r.label, data: r.months, backgroundColor: this.palette(i) })),
        },
        options: this.monthAxisOptions(),
        plugins: [BAR_VALUE_LABELS],
      };
    },
    // Monthly bar chart for a single category at the CURRENT scope (whatever
    // this.scope.districtId currently is — "all" or one specific district).
    // Built directly via monthlyUsed (already district-aware for kerja_rutin)
    // rather than going through budgetMonthMatrix's rowDim:"category" result
    // — simpler for a single-category chart, not required for correctness
    // (budgetMonthMatrix's cats list now includes kerja_rutin at a specific
    // district too, per the table-view fix).
    singleCategoryMonthBarBlock(category, cumulative) {
      const monthLbls = this.monthLabels.map((m) => m.label);
      let months = monthlyUsed(category, this.scope);
      if (cumulative) {
        let running = 0;
        months = months.map((v) => (running += v));
      }
      return {
        key: `single-${category}`,
        section: "",
        rowClass: "row g-3",
        title: `Monthly usage (${CATEGORY_LABELS[category]})`,
        type: "bar",
        col: "col-12",
        data: {
          labels: monthLbls,
          datasets: [{ label: CATEGORY_LABELS[category], data: months, backgroundColor: this.palette(0) }],
        },
        options: this.monthAxisOptions(),
        plugins: [BAR_VALUE_LABELS],
      };
    },
    // A Used-vs-Remaining utilization donut block (legend non-interactive).
    // `large` makes it the single HQ donut: wider column, taller canvas.
    // When used+remaining is 0 (e.g. no expenses AND no allocation) a doughnut
    // would draw nothing, so we render a full "Unused" placeholder ring instead
    // — that way a 100%-unused category still shows a chart.
    pieBlock(key, name, used, allocated, section, rowClass, large) {
      const u = used || 0;
      const remaining = Math.max((allocated || 0) - u, 0);
      const empty = u + remaining === 0;
      return {
        key,
        section,
        rowClass,
        title: `${name} · ${this.fmt(allocated)} · ${this.pct(used, allocated)} used`,
        type: "doughnut",
        col: large ? "col-12 col-lg-8 col-xl-6" : "col-6 col-md-4 col-lg-3",
        height: large ? 480 : undefined, // undefined -> VizChart's 320 default
        data: {
          labels: empty ? ["Unused"] : ["Used", "Remaining"],
          datasets: [
            {
              data: empty ? [1] : [u, remaining],
              backgroundColor: empty ? [REMAINING_COLOR] : [USED_COLOR, REMAINING_COLOR],
            },
          ],
        },
        options: {
          // Ring width follows Sebut Harga, which sets no cutout at all and
          // so inherits Chart.js's 50% doughnut default (INFS-1423).
          cutout: "50%",
          plugins: {
            legend: { position: "bottom", onClick: () => {} },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  if (empty) return "Unused (no allocation)";
                  const total = ctx.dataset.data.reduce((a, b) => a + (b || 0), 0);
                  const share = total > 0 ? ` (${((ctx.parsed / total) * 100).toFixed(1)}%)` : "";
                  return `${ctx.label}: ${this.fmt(ctx.parsed)}${share}`;
                },
              },
            },
          },
        },
      };
    },
    // Dashed guidance lines = expected pace. Per month: allocation / 12 (flat).
    // Cumulative: allocation / 12 * month (ramps up to the full allocation).
    // Skips categories with no ceiling at the current scope (e.g. kerja_rutin
    // at a specific district) — a flat "Expected: RM0.00" dashed line would be
    // misleading when there's genuinely no per-district target to pace against.
    guidanceLines(cats, cumulative) {
      return cats
        .filter((cat) => allocationForCategory(cat, this.scope) != null)
        .map((cat, i) => {
          const per = allocationForCategory(cat, this.scope) / 12;
          const data = Array.from({ length: 12 }, (_, mo) => (cumulative ? per * (mo + 1) : per));
          return {
            type: "line",
            label: `${CATEGORY_LABELS[cat]} Expected`,
            data,
            borderColor: this.palette(i),
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
          };
        });
    },
    // Compact RM label for axis ticks (no decimals, keeps the axis readable).
    rmAxis(v) {
      return "RM " + Number(v || 0).toLocaleString("en-MY");
    },
    monthAxisOptions() {
      return {
        plugins: {
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${this.fmt(ctx.parsed.y)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: "10%", // room for the bar amounts above the tallest bar
            title: { display: true, text: "Amount (RM)" },
            ticks: { callback: (v) => this.rmAxis(v) },
          },
          x: { title: { display: true, text: "Month" }, ticks: { align: "center" } },
        },
      };
    },
    // --- Excel export of the current table view ---
    downloadExcel() {
      const x = this.excelExport;
      downloadXlsx(x.filename, x.sheetName, x.headers, x.rows);
    },
  },
};
</script>

<style scoped>
/* Sticky pinned columns for the month matrix (mirrors the Expenses tab). */
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.hide-scrollbar::-webkit-scrollbar { width: 0; height: 0; }
.expense-matrix { border-collapse: separate; border-spacing: 0; }
.expense-matrix th, .expense-matrix td { background-color: #212529; border-right: 1px solid #495057; border-bottom: 1px solid #495057; }
.expense-matrix thead th { border-top: 1px solid #495057; }
.expense-matrix tr > :first-child { border-left: 1px solid #495057; }
.expense-matrix tfoot th, .expense-matrix tfoot td { background-color: #2c3034; }
.expense-matrix .cat-col, .expense-matrix .used-col, .expense-matrix .alloc-col, .expense-matrix .remain-col { position: sticky; z-index: 2; background-color: #1a1d20; }
.expense-matrix tfoot .cat-col, .expense-matrix tfoot .used-col, .expense-matrix tfoot .alloc-col, .expense-matrix tfoot .remain-col { background-color: #25292d; }
.expense-matrix .cat-col { left: 0; z-index: 3; width: 190px; min-width: 190px; box-shadow: 6px 0 8px -6px rgba(0,0,0,.7); }
.expense-matrix .used-col { right: 300px; width: 150px; min-width: 150px; box-shadow: -6px 0 8px -6px rgba(0,0,0,.7); }
.expense-matrix .alloc-col { right: 150px; width: 150px; min-width: 150px; }
.expense-matrix .remain-col { right: 0; width: 150px; min-width: 150px; }
.expense-matrix thead .cat-col, .expense-matrix thead .used-col, .expense-matrix thead .alloc-col, .expense-matrix thead .remain-col { z-index: 4; }

/* Chart subtabs as folder-index tabs: inactive tabs dimmed and set a little
   lower (margin, not transform, so the strip height never changes); the active
   tab is full colour and overlaps the panel's 3px top edge so there is no seam. */
.subtab-strip { align-items: flex-end; flex-wrap: wrap; }
/* One fixed width for every tab so the strip reads as a uniform row whatever
   the label length (Hanif, 19 Oct); wide enough for bold "Kerja Rutin" and
   "Kos Awalan", and the strip wraps on narrow screens rather than overflowing. */
.subtab-strip .subtab { width: 8rem; text-align: center; white-space: nowrap; }
.subtab-strip .subtab { position: relative; z-index: 1; color: #fff; background-color: var(--tab-dimmed); border: 0; border-radius: 8px 8px 0 0; padding: 0.3rem 0.5rem; margin-top: 0.2rem; }
/* Hover underlines rather than brightens: brightening the dimmed fill would
   drop white text below 4.5:1 on the lighter colours. */
.subtab-strip .subtab:hover { text-decoration: underline; }
.subtab-strip .subtab.active { color: #111; background-color: var(--tab-color); margin-top: 0; padding-top: 0.5rem; margin-bottom: -3px; padding-bottom: calc(0.3rem + 3px); font-weight: 600; }
.subtab-panel { border-top: 3px solid var(--tab-color, transparent); padding-top: 0.75rem; }
</style>
